import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import useMobile from '../../hooks/useMobile';
import { paths } from '../../config/paths';
import { Pagination } from '../../components/common/Pagination';
import { NoResults } from '../../components/common/NoResults';
import { useAuth } from '../../contexts/AuthProvider';
import { useLoginRequiredPopup } from '../../utils/auth/loginRequiredPopup';
import { useSystemPopup } from '../../components/common/SystemPopup';
import {
  DONATION_STORY_COMMENT_PAGE_SIZE,
  deleteDonationStoryComment,
  fetchDonationStoryComments,
  fetchDonationStoryDetail,
  postDonationStoryComment,
  putDonationStoryComment,
} from '../../api/info/donation';
import { formatDonationDate, rewriteLegacyFileImageUrlsInHtml } from '../../utils/donationMedia';
import Spinner from '../../components/common/Spinner';

const COMMENT_MAX = 200;
const LOGIN_PLACEHOLDER =
  '※ 로그인 후 등록 가능합니다.';
const INPUT_PLACEHOLDER =
  '※ 연락처, 주소, 이메일 등의 내용을 입력하시는 경우 개인정보가 노출될 수 있으니, 게시를 삼가해 주시기 바랍니다.';

export default function DonationStoryDetail() {
  const { isMobile } = useMobile();
  const { id: storyNo } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { open: openSystemPopup } = useSystemPopup();
  const openLoginRequiredPopup = useLoginRequiredPopup();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentPage, setCommentPage] = useState(1);
  const [comments, setComments] = useState([]);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editTexts, setEditTexts] = useState({});

  const commentTotalPages = Math.max(
    1,
    Math.ceil(commentTotal / DONATION_STORY_COMMENT_PAGE_SIZE)
  );

  const loadComments = useCallback(async () => {
    const data = await fetchDonationStoryComments(
      storyNo,
      commentPage,
      DONATION_STORY_COMMENT_PAGE_SIZE
    );
    setComments(Array.isArray(data?.items) ? data.items : []);
    setCommentTotal(Number(data?.totalCount) || 0);
  }, [storyNo, commentPage]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await fetchDonationStoryDetail(storyNo);
        if (cancelled) return;
        if (!d) {
          navigate(paths.info.donationStory, { replace: true });
          return;
        }
        setDetail(d);
      } catch {
        if (!cancelled) navigate(paths.info.donationStory, { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [storyNo, navigate]);

  useEffect(() => {
    if (!storyNo || loading) return;
    loadComments().catch(() => {
      setComments([]);
      setCommentTotal(0);
    });
  }, [storyNo, loading, loadComments]);

  const requireLogin = useCallback(() => {
    if (isLoggedIn) return true;
    openLoginRequiredPopup();
    return false;
  }, [isLoggedIn, openLoginRequiredPopup]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!requireLogin()) return;
    const cont = commentText.trim();
    if (!cont) {
      openSystemPopup({
        type: 'alert',
        content: <p className="strong-text">댓글 내용을 입력해 주세요.</p>,
      });
      return;
    }
    openSystemPopup({
      type: 'confirm',
      content: <p className="strong-text">댓글을 등록하시겠습니까?</p>,
      onConfirm: async () => {
        try {
          await postDonationStoryComment(storyNo, cont);
          setCommentText('');
          const data = await fetchDonationStoryComments(storyNo, 1, DONATION_STORY_COMMENT_PAGE_SIZE);
          setCommentPage(1);
          setComments(Array.isArray(data?.items) ? data.items : []);
          setCommentTotal(Number(data?.totalCount) || 0);
          openSystemPopup({
            type: 'alert',
            content: <p className="strong-text">등록되었습니다.</p>,
          });
        } catch (err) {
          openSystemPopup({
            type: 'alert',
            content: <p className="strong-text">{err.message || '등록에 실패하였습니다.'}</p>,
          });
        }
      },
    });
  };

  const handleUpdateComment = (cmntNo) => {
    if (!requireLogin()) return;
    const cont = (editTexts[cmntNo] ?? '').trim();
    if (!cont) {
      openSystemPopup({
        type: 'alert',
        content: <p className="strong-text">댓글 내용을 입력해 주세요.</p>,
      });
      return;
    }
    openSystemPopup({
      type: 'confirm',
      content: <p className="strong-text">댓글을 수정하시겠습니까?</p>,
      onConfirm: async () => {
        try {
          await putDonationStoryComment(storyNo, cmntNo, cont);
          setEditingCommentId(null);
          await loadComments();
          openSystemPopup({
            type: 'alert',
            content: <p className="strong-text">수정되었습니다.</p>,
          });
        } catch (err) {
          openSystemPopup({
            type: 'alert',
            content: <p className="strong-text">{err.message || '수정에 실패하였습니다.'}</p>,
          });
        }
      },
    });
  };

  const handleDeleteComment = (cmntNo) => {
    if (!requireLogin()) return;
    openSystemPopup({
      type: 'confirm',
      content: <p className="strong-text">정말로 댓글을 삭제하시겠습니까?</p>,
      onConfirm: async () => {
        try {
          await deleteDonationStoryComment(storyNo, cmntNo);
          await loadComments();
          openSystemPopup({
            type: 'alert',
            content: <p className="strong-text">삭제되었습니다.</p>,
          });
        } catch (err) {
          openSystemPopup({
            type: 'alert',
            content: <p className="strong-text">{err.message || '삭제에 실패하였습니다.'}</p>,
          });
        }
      },
    });
  };

  if (loading || !detail) {
    return (
      <>
        <h2 className="page-title">행복나눔 100% 스토리</h2>
        <div className="activity-list-loading" aria-busy="true" aria-live="polite">
          <Spinner size="lg" />
        </div>
      </>
    );
  }

  return (
    <>
      <h2 className="page-title">행복나눔 100% 스토리</h2>

      <section className="data__detail">
        <div className="data__detail-head">
          <h3 className="title">{detail.title}</h3>
          <p className="info">
            <span>{formatDonationDate(detail.creDt)}</span>
          </p>
        </div>

        <div
          className="data__detail-content"
          dangerouslySetInnerHTML={{
            __html: rewriteLegacyFileImageUrlsInHtml(detail.cont),
          }}
        />

        {isMobile && <hr className="divider mg-none" />}
        <div className="data__detail-reply">
          <h4 className="title">한줄 댓글</h4>
          <form className="write" onSubmit={handleSubmitComment}>
            <div className="field">
              <textarea
                className="textarea"
                rows={6}
                aria-label="댓글 작성"
                maxLength={COMMENT_MAX}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onClick={() => { if (!isLoggedIn) requireLogin(); }}
                placeholder={isLoggedIn ? INPUT_PLACEHOLDER : LOGIN_PLACEHOLDER}
                readOnly={!isLoggedIn}
              />
              <div className="textarea-count">
                <small>({commentText.length}/{COMMENT_MAX})</small>
              </div>
            </div>
            <button type="submit" className="btn">등록하기</button>
          </form>

          {comments.length > 0 ? (
            <ul className="reply-list">
              {comments.map((item) => {
                const isMine = item.isMyCmntYn === 'Y';
                const isEditing = editingCommentId === item.cmntNo;
                return (
                  <li className="item" key={item.cmntNo}>
                    <span className="name">{item.creNm || '(탈퇴패널)'}</span>
                    <div className="info">
                      {isMine && isEditing ? (
                        <textarea
                          className="textarea"
                          rows={3}
                          aria-label="댓글 수정"
                          maxLength={COMMENT_MAX}
                          value={editTexts[item.cmntNo] ?? item.cont ?? ''}
                          onChange={(e) =>
                            setEditTexts((prev) => ({ ...prev, [item.cmntNo]: e.target.value }))
                          }
                        />
                      ) : (
                        <p className="content">{item.cont}</p>
                      )}
                      <span className="date">{formatDonationDate(item.creDt)}</span>
                      {isMine && (
                        isEditing ? (
                          <>
                            <button type="button" className="btn" onClick={() => handleUpdateComment(item.cmntNo)}>
                              확인
                            </button>
                            <button type="button" className="btn" onClick={() => setEditingCommentId(null)}>
                              취소
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn"
                              onClick={() => {
                                setEditingCommentId(item.cmntNo);
                                setEditTexts((prev) => ({
                                  ...prev,
                                  [item.cmntNo]: item.cont ?? '',
                                }));
                              }}
                            >
                              수정
                            </button>
                            <button type="button" className="btn" onClick={() => handleDeleteComment(item.cmntNo)}>
                              삭제
                            </button>
                          </>
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <NoResults>작성된 댓글이 없습니다.</NoResults>
          )}

          {commentTotal > DONATION_STORY_COMMENT_PAGE_SIZE && (
            <Pagination
              pageActive={commentPage}
              totalCount={commentTotalPages}
              onPageChange={setCommentPage}
            />
          )}
        </div>

        {isMobile && <hr className="divider mg-type" />}

        <div className={`data__action-buttons${isMobile ? ' flex-column' : ''}`}>
          <Link to={paths.info.donationStory} className="btn btn--lg btn--light">
            목록으로
          </Link>
        </div>
      </section>
    </>
  );
}
